import { CONSENT_TCPA_KEY, CONSENT_TO_TREAT_KEY } from '@act/shared/constants';
import {
  Backdrop,
  SpeedDial,
  SpeedDialAction,
  Typography,
  speedDialActionClasses,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import { clientAppSelectors } from 'redux/slices/clientSlice';
import { consentAppSelectors } from 'redux/slices/consent.slice';
import { selectSelectedMember } from 'redux/slices/memberSlice';
import { useAppSelector } from 'redux/store-hooks';
import styled from 'styled-components';
import { reemaColors, themeColors } from 'toolkit/styles/colors';
import { useTracking } from 'react-components/tracking';
import useStartProviderScheduling from './useStartProviderScheduling';
import { IconLibrary } from '@act/storybook';

const NoWrapTypography = styled(Typography)`
  white-space: nowrap;
  color: ${themeColors.onSurface};
`;

const NewActionPanel = () => {
  const theme = useTheme();
  const {
    createScheduledEvent,
    createVisit,
    createNote,
    createMessage,
    createCall,
  } = useInteractionsDialog();
  const { component: providerSchedulingComponent, startProviderScheduling } =
    useStartProviderScheduling();
  const { generateTrackingProps } = useTracking();

  const [open, setOpen] = useState<boolean>(false);
  const selectedMember = useAppSelector(selectSelectedMember);
  const client = clientAppSelectors.useSelectClientById(
    selectedMember?.clientId
  );
  const consentToTreatTerm =
    consentAppSelectors.useSelectTermByKey(CONSENT_TO_TREAT_KEY);
  const tcpaTerm = consentAppSelectors.useSelectTermByKey(CONSENT_TCPA_KEY);
  const consentToTreatRejected = consentToTreatTerm?.consented === false;
  const hasProviderAvailable = !!client?.avonMedicalCenterId;
  const canSendMessages = tcpaTerm?.consented !== false;

  const onOpen = (event, reason) => {
    if (reason !== 'focus') {
      // ignore focus reason because then the options show again when you exit out of the chosen dialog
      setOpen(true);
    }
  };
  const onClose = () => setOpen(false);
  const onSelect = action_fab => {
    setOpen(false);
    action_fab.action();
  };

  let ACTION_FABS = [
    {
      actionType: 'Phone call',
      trackingName: 'phone-call-action',
      action: () => createCall({ user: selectedMember }),
      icon: <IconLibrary iconName="Phone" />,
    },
    {
      actionType: 'Text message',
      trackingName: 'text-message-action',
      disabled: !canSendMessages,
      disabledText: 'TCPA required to text message',
      action: () => createMessage({ user: selectedMember }),
      icon: <IconLibrary iconName="ChatDots" />,
    },
    {
      actionType: 'Visit',
      trackingName: 'visit-action',
      action: () => createVisit({ userId: selectedMember.id }),
      icon: <IconLibrary iconName="HouseLine" />,
    },
    {
      actionType: 'Note',
      trackingName: 'note-action',
      action: () => createNote({ user: selectedMember }),
      icon: <IconLibrary iconName="Note" />,
    },
    hasProviderAvailable && {
      actionType: 'Provider scheduling',
      trackingName: 'provider-scheduling-action',
      disabledText: 'Consent required for provider scheduling',
      action: startProviderScheduling,
      disabled: consentToTreatRejected,
      icon: <IconLibrary iconName="FirstAid" />,
    },
    {
      actionType: 'Scheduled event',
      trackingName: 'scheduled-event-action',
      action: () => createScheduledEvent({ user: selectedMember }),
      icon: <IconLibrary iconName="CalendarDots" />,
    },
  ];
  ACTION_FABS = ACTION_FABS.filter(fab => fab);
  const disabledStyle = {
    backgroundColor: '#c5c7c8',
    color: reemaColors.darkGray,
  };

  return (
    <>
      <Backdrop open={open} sx={{ zIndex: 1 }} />
      <SpeedDial
        ariaLabel="New interaction"
        icon={<IconLibrary iconName="Plus" weight="regular" useContrastColor />}
        onOpen={onOpen}
        onClose={onClose}
        open={open}
        sx={{
          position: 'fixed',
          right: '24px',
          bottom: '24px',
          zIndex: 1000,
        }}
      >
        {ACTION_FABS.map(fab => (
          <SpeedDialAction
            key={`${fab.actionType}`}
            sx={{
              ...(fab.disabled && {
                [`& .${speedDialActionClasses.staticTooltipLabel}`]:
                  disabledStyle,
                [`& .${speedDialActionClasses.fab}`]: disabledStyle,
              }),
            }}
            icon={fab.icon}
            onClick={() => !fab.disabled && onSelect(fab)}
            tooltipTitle={
              <NoWrapTypography variant="labelMedium">
                {(fab.disabled && fab.disabledText) || fab.actionType}
              </NoWrapTypography>
            }
            FabProps={{
              ...(fab.disabled ? { disabled: true } : {}),
              ...generateTrackingProps(fab.trackingName),
            }}
            tooltipOpen
          />
        ))}
      </SpeedDial>
      {providerSchedulingComponent}
    </>
  );
};

export default NewActionPanel;
